import { useState, useEffect, useRef, useCallback } from 'react';

const FONT_M = "'Space Mono', monospace";

const BASE  = [' ', '(', ')', ' ', '·', '—', ' ', '[', ']', ' ', '+', '|', ' ', '  '];
const WORDS = [
  'ETH', 'BTC', 'IRL', 'CFW', 'PLT',
  'PILL', 'HEAD', 'STACY', 'VERSE',
  '2021', '6655', '200', '111', '19K',
];

/* Grid dimensions match the pre font metrics */
const CELL_W = 7.2;
const CELL_H = 15.4;

function buildGrid(cols, rows) {
  return Array.from({ length: rows }, () => {
    const row = [];
    let c = 0;
    while (c < cols) {
      if (Math.random() < 0.005 && c + 6 < cols) {
        const word = WORDS[Math.floor(Math.random() * WORDS.length)];
        for (let k = 0; k < word.length && c < cols; k++, c++) {
          row.push(word[k]);
        }
      } else {
        row.push(BASE[Math.floor(Math.random() * BASE.length)]);
        c++;
      }
    }
    return row.slice(0, cols);
  });
}

function shiftLeft(grid) {
  return grid.map(row => {
    const next = row.slice(1);
    next.push(
      Math.random() < 0.003
        ? WORDS[Math.floor(Math.random() * WORDS.length)][0]
        : BASE[Math.floor(Math.random() * BASE.length)]
    );
    return next;
  });
}

function mutateOne(grid) {
  const copy = grid.map(r => [...r]);
  const row  = Math.floor(Math.random() * copy.length);
  const maxC = Math.max(1, copy[0].length - 5);
  const col  = Math.floor(Math.random() * maxC);
  const word = WORDS[Math.floor(Math.random() * WORDS.length)];
  for (let k = 0; k < word.length && col + k < copy[row].length; k++) {
    copy[row][col + k] = word[k];
  }
  return copy;
}

function debounce(fn, ms) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
}

export default function AsciiField({ isIdle, isActive = false, disturbPoint }) {
  const gridRef         = useRef(null);
  const sparkTimerRef   = useRef(null);
  const sparkReturnRef  = useRef(null);
  const disturbTimersRef = useRef([]);
  const [display, setDisplay]     = useState('');
  const [sparkCell, setSparkCell] = useState(null);

  /* ─── Disturbance: rapid mutation around a cell region ─── */
  const disturb = useCallback((centerRow, centerCol, cellRadius, duration = 500) => {
    if (!gridRef.current) return;
    const grid = gridRef.current;
    const totalRows = grid.length;
    const totalCols = grid[0]?.length ?? 0;

    /* Collect affected cells */
    const affected = [];
    for (let dr = -cellRadius; dr <= cellRadius; dr++) {
      for (let dc = -cellRadius; dc <= cellRadius; dc++) {
        if (Math.sqrt(dr * dr + dc * dc) > cellRadius) continue;
        const r = centerRow + dr;
        const c = centerCol + dc;
        if (r >= 0 && r < totalRows && c >= 0 && c < totalCols) {
          affected.push([r, c]);
        }
      }
    }
    if (!affected.length) return;

    const mutateId = setInterval(() => {
      if (!gridRef.current) return;
      const copy = gridRef.current.map(r => [...r]);
      affected.forEach(([r, c]) => {
        copy[r][c] = BASE[Math.floor(Math.random() * BASE.length)];
      });
      gridRef.current = copy;
      setDisplay(copy.map(r => r.join('')).join('\n'));
    }, 80);

    disturbTimersRef.current.push(mutateId);
    const cleanId = setTimeout(() => {
      clearInterval(mutateId);
      disturbTimersRef.current = disturbTimersRef.current.filter(id => id !== mutateId);
    }, duration);
    disturbTimersRef.current.push(cleanId);
  }, []);

  /* ─── Effect 1: build initial grid + resize ─── */
  useEffect(() => {
    function init() {
      const cols = Math.floor(window.innerWidth / CELL_W);
      const rows = Math.floor(window.innerHeight / CELL_H);
      gridRef.current = buildGrid(cols, rows);
      setDisplay(gridRef.current.map(r => r.join('')).join('\n'));
    }
    init();
    const onResize = debounce(init, 200);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─── Effect 2: shift/mutate intervals — only while idle ─── */
  useEffect(() => {
    if (!isIdle || !gridRef.current) return;

    const shiftTimer = setInterval(() => {
      gridRef.current = shiftLeft(gridRef.current);
      setDisplay(gridRef.current.map(r => r.join('')).join('\n'));
    }, 2400);

    const mutateTimer = setInterval(() => {
      gridRef.current = mutateOne(gridRef.current);
      setDisplay(gridRef.current.map(r => r.join('')).join('\n'));
    }, 6000);

    return () => {
      clearInterval(shiftTimer);
      clearInterval(mutateTimer);
    };
  }, [isIdle]);

  /* ─── Effect 3: spark + ripple ─── */
  useEffect(() => {
    if (!isIdle) {
      clearTimeout(sparkTimerRef.current);
      clearTimeout(sparkReturnRef.current);
      setSparkCell(null);
      return;
    }

    function fireSpark() {
      if (!gridRef.current) return;
      const rows = gridRef.current.length;
      const cols = gridRef.current[0]?.length ?? 0;
      if (!rows || !cols) return;

      const row  = Math.floor(Math.random() * rows);
      const col  = Math.floor(Math.random() * cols);
      const char = gridRef.current[row][col].trim() || '·';

      setSparkCell({ row, col, char });

      /* Ripple — 3 rings outward from spark */
      disturb(row, col, 3, 600);

      sparkReturnRef.current = setTimeout(() => {
        setSparkCell(null);
        sparkTimerRef.current = setTimeout(fireSpark, 3200);
      }, 120);
    }

    sparkTimerRef.current = setTimeout(fireSpark, 3200);

    return () => {
      clearTimeout(sparkTimerRef.current);
      clearTimeout(sparkReturnRef.current);
      setSparkCell(null);
    };
  }, [isIdle, disturb]);

  /* ─── Effect 4: disturbPoint from node hover ─── */
  useEffect(() => {
    if (!disturbPoint || !gridRef.current) return;
    const col = Math.round(disturbPoint.x / CELL_W);
    const row = Math.round(disturbPoint.y / CELL_H);
    const cellRadius = Math.ceil(80 / CELL_W);
    disturb(row, col, cellRadius, 400);
  }, [disturbPoint, disturb]);

  /* ─── Cleanup disturbance timers on unmount ─── */
  useEffect(() => {
    return () => disturbTimersRef.current.forEach(id => { clearTimeout(id); clearInterval(id); });
  }, []);

  const opacity = isActive ? 0 : (isIdle ? 0.04 : 0.02);

  return (
    <>
      {/* Main ASCII field */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 5,
          pointerEvents: 'none',
          opacity,
          transition: 'opacity 0.3s steps(4)',
          overflow: 'hidden',
        }}
      >
        <pre
          style={{
            fontFamily: FONT_M,
            fontSize: 11,
            lineHeight: 1.4,
            color: '#0D0D0D',
            whiteSpace: 'pre',
            width: '100vw',
            height: '100vh',
            margin: 0,
            padding: 0,
            overflow: 'hidden',
          }}
        >
          {display}
        </pre>
      </div>

      {/* Spark overlay */}
      {sparkCell && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top:  sparkCell.row * CELL_H,
            left: sparkCell.col * CELL_W,
            zIndex: 5,
            fontFamily: FONT_M,
            fontSize: 11,
            lineHeight: 1.4,
            color: '#0D0D0D',
            opacity: 0.35,
            pointerEvents: 'none',
          }}
        >
          {sparkCell.char}
        </div>
      )}
    </>
  );
}
